/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef } from 'react';
import {
  Mic,
  FileText,
  User,
  Calendar,
  ClipboardList,
  Brain,
  MessageSquare,
  Activity,
  Pill,
  FlaskConical,
  X,
  Users,
  Sparkles,
  AlertCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { usePatientStore } from '../stores/usePatientStore';
import { useAppointmentStore } from '../stores/useAppointmentStore';
import { useMedicalRecordStore } from '../stores/useMedicalRecordStore';
import { useAIStore } from '../stores/useAIStore';
import { getLocalizedValue } from '../utils/localization';
import { useLocalizationStore } from '../stores/useLocalizationStore';
import { useVoiceNavigationStore } from '../stores/useVoiceNavigationStore';
import { Button } from '@/components/ui/button';
import { TextInput } from '@/components/ui/text-input';
import { usePageContext } from '../hooks/usePageContext';
import { useAuthStore } from '../stores/useAuthStore';
import { aiGatewayService } from '../services/aiGateway';
import { patientApi, type Patient } from '../services/patientApi';
import {
  resolvePatientName,
  findPatientByName,
} from '../utils/patientName';
import { homeRoute } from '@/config/homeRoute';
import { useShellIdentity } from '@/components/shell/useShellIdentity';

interface AICommand {
  id: string;
  title: string;
  description: string;
   
  icon: React.FC<any>;
  category: 'analysis' | 'generation' | 'action' | 'navigation';
  action: () => void;
  keywords: string[];
}

interface SearchResult {
  id: string;
  title: string;
  subtitle: string;
  type: 'patient' | 'appointment' | 'record';
   
  icon: React.FC<any>;
  route: string;
}

interface AICommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AICommandPalette: React.FC<AICommandPaletteProps> = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  /* Where "back" goes — the role's own first door under the design shell,
     not the legacy dashboard. See homeRoute. */
  const { surface } = useShellIdentity();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isListening, setIsListening] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [aiParsing, setAiParsing] = useState(false);
   
  const [aiSuggestion, setAiSuggestion] = useState<any>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const queryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pageContext = usePageContext(); // Get current page context
  const currentLang = useLocalizationStore.getState().getCurrentLanguage();
  const currentUser = useAuthStore(state => state.user); // Get logged-in user
  const selectedPatient = usePatientStore(state => state.selectedPatient);
  // In-memory store — still used to resolve patient names on appointment/record
  // results and in the AI-autofill fallback. Patient *search* now goes through
  // the server (below) since the store is never roster-hydrated.
  const patients = usePatientStore(state => state.patients);
  const appointments = useAppointmentStore(state => state.appointments);
  const medicalRecords = useMedicalRecordStore(state => state.medicalRecords);

  // Server-side patient search. The palette is mounted globally and the
  // in-memory patient store only holds a handful of recently-touched patients,
  // so searching it left the palette blank for the rest of the roster. Query
  // `/patients?search=` directly (debounced, gated on open + 2 chars) so the
  // whole roster is reachable — mirrors PatientSearchCombobox / ReferralFormModal.
  const [patientResults, setPatientResults] = useState<Patient[]>([]);
  useEffect(() => {
    const q = query.trim();
    if (!isOpen || q.length < 2) {
      setPatientResults([]);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(() => {
      patientApi
        .getPatients({ search: q, limit: 8 })
        .then((res) => {
          if (!cancelled) setPatientResults(res.data ?? []);
        })
        .catch(() => {
          if (!cancelled) setPatientResults([]);
        });
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query, isOpen]);

  // Dynamic commands based on page context
  const getPageCommands = (): AICommand[] => {
    const baseCommands: AICommand[] = [];

    // Page-specific commands (highest priority)
    if (pageContext.pageType === 'patient' && pageContext.currentPatient) {
      // Use selectedPatient from store if available (most up-to-date), fallback to pageContext
      const patient = selectedPatient ? {
        id: selectedPatient.id,
        // Handle both API format (firstName/lastName) and mock format (name object)
        name: (selectedPatient as any).firstName
          ? `${(selectedPatient as any).firstName} ${(selectedPatient as any).lastName}`.trim()
          : typeof selectedPatient.name === 'string'
            ? selectedPatient.name
            : getLocalizedValue(selectedPatient.name, currentLang),
        age: pageContext.currentPatient.age,
        gender: pageContext.currentPatient.gender
      } : pageContext.currentPatient;

      console.log('🎯 Command Palette Patient Context:', {
        selectedPatient: selectedPatient?.id,
        selectedPatientHasFirstName: !!(selectedPatient as any)?.firstName,
        selectedPatientName: patient.name,
        pageContextPatient: pageContext.currentPatient.id,
        pageContextPatientName: pageContext.currentPatient.name,
        resolvedPatientName: patient.name
      });

      // Only show patient-specific commands if we have a valid patient name
      if (!patient.name || patient.name === 'Unknown') {
        console.log('⚠️ Patient name not available yet, hiding patient-specific commands');
        return baseCommands;
      }

      baseCommands.push({
        id: 'schedule-appointment-patient',
        title: t('commandPalette.scheduleAppointmentFor', { patientName: patient.name }),
        description: t('commandPalette.bookAppointment'),
        icon: Calendar,
        category: 'action',
        action: () => {
          navigate('/appointments/new', { state: { patientId: patient.id, patientName: patient.name } });
          onClose();
        },
        keywords: ['appointment', 'schedule', 'book', patient.name]
      });

      baseCommands.push({
        id: 'order-labs-patient',
        title: t('commandPalette.orderLabTestsFor', { patientName: patient.name }),
        description: t('commandPalette.createLabOrder'),
        icon: FlaskConical,
        category: 'action',
        action: () => {
          navigate('/lab-orders/new', { state: { patientId: patient.id, patientName: patient.name } });
          onClose();
        },
        keywords: ['lab', 'test', 'order', patient.name]
      });

      baseCommands.push({
        id: 'prescribe-patient',
        title: t('commandPalette.prescribeMedicationFor', { patientName: patient.name }),
        description: t('commandPalette.createPrescription'),
        icon: Pill,
        category: 'action',
        action: () => {
          navigate('/prescriptions/new', { state: { patientId: patient.id, patientName: patient.name } });
          onClose();
        },
        keywords: ['prescription', 'medication', 'drug', patient.name]
      });
    }

    if (pageContext.pageType === 'lab-order' && pageContext.labOrderId) {
      baseCommands.push({
        id: 'interpret-lab-results',
        title: t('commandPalette.interpretLabResults'),
        description: t('commandPalette.aiAnalysisLabOrder'),
        icon: FlaskConical,
        category: 'analysis',
        action: () => {
          // Trigger AI analysis
          useAIStore.getState().triggerAnalysis({
            analysisType: 'lab-results-interpretation',
            context: {
              labOrderId: pageContext.labOrderId,
              patientId: pageContext.patientId
            }
          });
          onClose();
        },
        keywords: ['interpret', 'analyze', 'lab', 'results']
      });
    }

    if (pageContext.pageType === 'medical-record') {
      baseCommands.push({
        id: 'analyze-record',
        title: t('commandPalette.analyzeMedicalRecord'),
        description: t('commandPalette.aiClinicalAssessment'),
        icon: FileText,
        category: 'analysis',
        action: () => {
          useAIStore.getState().triggerAnalysis({
            analysisType: 'patient-clinical-assessment',
            context: {
              medicalRecordId: pageContext.medicalRecordId,
              patientId: pageContext.patientId
            }
          });
          onClose();
        },
        keywords: ['analyze', 'assessment', 'record']
      });
    }

    return baseCommands;
  };

  // Get page-specific commands
  const pageCommands = getPageCommands();

  // Context-aware general commands
  const getContextualCommands = (): AICommand[] => {
    const contextCommands: AICommand[] = [];

    // PATIENT-SPECIFIC ANALYSIS COMMANDS (only on patient pages)
    if (pageContext.pageType === 'patient' && pageContext.currentPatient) {
      // Use selectedPatient from store if available (most up-to-date), fallback to pageContext
      const patient = selectedPatient ? {
        id: selectedPatient.id,
        // Handle both API format (firstName/lastName) and mock format (name object)
        name: (selectedPatient as any).firstName
          ? `${(selectedPatient as any).firstName} ${(selectedPatient as any).lastName}`.trim()
          : typeof selectedPatient.name === 'string'
            ? selectedPatient.name
            : getLocalizedValue(selectedPatient.name, currentLang),
        age: pageContext.currentPatient.age,
        gender: pageContext.currentPatient.gender
      } : pageContext.currentPatient;

      // Only show patient-specific commands if we have a valid patient name
      if (!patient.name || patient.name === 'Unknown') {
        return contextCommands;
      }

      contextCommands.push({
        id: 'summarize-patient',
        title: t('commandPalette.summarizePatientHistory', { patientName: patient.name }),
        description: t('commandPalette.comprehensivePatientSummary'),
        icon: FileText,
        category: 'analysis',
        action: () => {
          if (selectedPatient) {
            useAIStore.getState().addContext({
              id: `patient-summary-${Date.now()}`,
              type: 'patient',
              data: selectedPatient,
              label: `Summarize ${getLocalizedValue(selectedPatient.name, currentLang)}'s history`
            });
            onClose();
          }
        },
        keywords: ['summary', 'patient', 'history', 'overview', patient.name]
      });

      contextCommands.push({
        id: 'medication-review-patient',
        title: t('commandPalette.reviewMedications', { patientName: patient.name }),
        description: t('commandPalette.checkInteractions'),
        icon: Pill,
        category: 'analysis',
        action: () => {
          // Trigger medication review analysis
          useAIStore.getState().triggerAnalysis({
            analysisType: 'medication-review',
            context: {
              patientId: patient.id,
              patientName: patient.name
            }
          });
          onClose();
        },
        keywords: ['medication', 'drug', 'interaction', 'prescription', patient.name]
      });

      contextCommands.push({
        id: 'analyze-risk-patient',
        title: t('commandPalette.analyzeRiskFactors', { patientName: patient.name }),
        description: t('commandPalette.riskAssessment'),
        icon: AlertCircle,
        category: 'analysis',
        action: () => {
          // Trigger risk factor analysis
          useAIStore.getState().triggerAnalysis({
            analysisType: 'risk-assessment',
            context: {
              patientId: patient.id,
              patientName: patient.name
            }
          });
          onClose();
        },
        keywords: ['risk', 'assessment', 'factors', patient.name]
      });
    }

    // LAB ANALYSIS COMMANDS (only on lab-order or patient pages)
    if (pageContext.pageType === 'lab-order' || pageContext.pageType === 'patient') {
      contextCommands.push({
        id: 'analyze-labs',
        title: t('commandPalette.analyzeRecentLabs'),
        description: t('commandPalette.aiLabAnalysis'),
        icon: FlaskConical,
        category: 'analysis',
        action: () => {
          navigate('/lab-orders');
          onClose();
        },
        keywords: ['lab', 'results', 'analysis', 'blood', 'test']
      });
    }

    return contextCommands;
  };

  // GENERATION COMMANDS (context-aware)
  const getGenerationCommands = (): AICommand[] => {
    const genCommands: AICommand[] = [];

    // Clinical note generation (only on medical-record or patient pages)
    if (pageContext.pageType === 'medical-record' || pageContext.pageType === 'patient') {
      genCommands.push({
        id: 'generate-note',
        title: t('commandPalette.generateClinicalNote'),
        description: t('commandPalette.startVisitRecording'),
        icon: ClipboardList,
        category: 'generation',
        action: () => {
          // Start visit recording with patient context if available
          // Prioritize selectedPatient from store (has actual patient data)
          const patientId = selectedPatient?.id || pageContext.currentPatient?.id;
          const patientName = selectedPatient
            ? ((selectedPatient as any).firstName
              ? `${(selectedPatient as any).firstName} ${(selectedPatient as any).lastName}`.trim()
              : typeof selectedPatient.name === 'string'
                ? selectedPatient.name
                : getLocalizedValue(selectedPatient.name, currentLang))
            : pageContext.currentPatient?.name;

          useAIStore.getState().startVisitRecording(patientId, patientName);
          useAIStore.getState().setShowAssistant(true); // Show the assistant
          onClose();
        },
        keywords: ['note', 'clinical', 'documentation', 'write', 'record', 'visit']
      });
    }

    // Discharge summary (only when patient has active admission)
    if (pageContext.pageType === 'patient' && pageContext.currentPatient) {
      // Use selectedPatient from store if available (most up-to-date), fallback to pageContext
      const patient = selectedPatient ? {
        id: selectedPatient.id,
        // Handle both API format (firstName/lastName) and mock format (name object)
        name: (selectedPatient as any).firstName
          ? `${(selectedPatient as any).firstName} ${(selectedPatient as any).lastName}`.trim()
          : typeof selectedPatient.name === 'string'
            ? selectedPatient.name
            : getLocalizedValue(selectedPatient.name, currentLang),
      } : pageContext.currentPatient;

      // Only show patient-specific generation commands if we have a valid patient name
      if (patient.name && patient.name !== 'Unknown') {
        genCommands.push({
          id: 'discharge-summary',
          title: t('commandPalette.generateDischargeSummary'),
          description: t('commandPalette.createDischargeDoc'),
          icon: FileText,
          category: 'generation',
          action: () => {
            // Open AI assistant with prompt to generate discharge summary
            useAIStore.getState().addContext({
              id: `discharge-summary-${Date.now()}`,
              type: 'patient',
              data: selectedPatient || patient,
              label: `Generate discharge summary for ${patient.name}`
            });
            useAIStore.getState().setPendingPrompt(
              `Generate a comprehensive discharge summary for ${patient.name} including: diagnosis, treatment provided, medications prescribed, follow-up care instructions, and discharge date.`
            );
            useAIStore.getState().setShowAssistant(true);
            onClose();
          },
          keywords: ['discharge', 'summary', 'release']
        });

        genCommands.push({
          id: 'referral-letter',
          title: t('commandPalette.generateReferralLetter'),
          description: t('commandPalette.createReferral'),
          icon: MessageSquare,
          category: 'generation',
          action: () => {
            // Open AI assistant with prompt to generate referral letter
            useAIStore.getState().addContext({
              id: `referral-letter-${Date.now()}`,
              type: 'patient',
              data: selectedPatient || patient,
              label: `Generate referral letter for ${patient.name}`
            });
            useAIStore.getState().setPendingPrompt(
              `Generate a referral letter for ${patient.name}. Include patient history, reason for referral, and relevant clinical findings.`
            );
            useAIStore.getState().setShowAssistant(true);
            onClose();
          },
          keywords: ['referral', 'letter', 'specialist']
        });
      }
    }

    return genCommands;
  };

  // UNIVERSAL ACTION COMMANDS (always available)
  const universalCommands: AICommand[] = [
    {
      id: 'new-patient',
      title: t('commandPalette.addNewPatient'),
      description: t('commandPalette.registerNewPatient'),
      icon: User,
      category: 'action',
      action: () => {
        navigate('/patients/new');
        onClose();
      },
      keywords: ['new', 'patient', 'add', 'register']
    },
    {
      id: 'schedule-appointment',
      title: t('commandPalette.scheduleAppointment'),
      description: t('commandPalette.bookNewAppointment'),
      icon: Calendar,
      category: 'action',
      action: () => {
        navigate('/appointments/new');
        onClose();
      },
      keywords: ['appointment', 'schedule', 'book', 'calendar']
    },
    {
      id: 'go-dashboard',
      title: t('commandPalette.goToDashboard'),
      description: t('commandPalette.navigateToDashboard'),
      icon: Activity,
      category: 'navigation',
      action: () => {
        navigate(homeRoute(surface));
        onClose();
      },
      keywords: ['dashboard', 'home', 'main']
    }
  ];

  // Combine ALL commands (priority order: page-specific → contextual → generation → universal)
  const commands: AICommand[] = [
    ...pageCommands,
    ...getContextualCommands(),
    ...getGenerationCommands(),
    ...universalCommands
  ];

  // Search for patients, appointments, and records
  const searchResults: SearchResult[] = [];

  if (query.trim()) {
    const searchTerm = query.toLowerCase();

    // Search patients — server-side results (already filtered by the query
    // against name/phone/email/MRN), so render them directly instead of
    // re-filtering in memory. The resolver handles the API name shape.
    patientResults.forEach(patient => {
      if (!patient) return;
      const patientName = resolvePatientName(
        patient as unknown as Record<string, unknown>,
        currentLang === 'ar',
      );
      if (!patientName) return;

      const p: any = patient;
      const phone = p.phone ?? p.phoneNumber ?? '';
      const email = p.email ?? '';
      const sub = [email, phone].filter(Boolean).join(' • ');
      searchResults.push({
        id: patient.id,
        title: patientName,
        subtitle: sub,
        type: 'patient',
        icon: Users,
        route: `/patients/${patient.id}`
      });
    });

    // Search appointments
    appointments.forEach(apt => {
      if (!apt || !apt.reason) return;
      const patient = patients.find(p => p.id === apt.patientId);
      const patientName = patient
        ? (resolvePatientName(patient as unknown as Record<string, unknown>, currentLang === 'ar') || 'Unknown Patient')
        : 'Unknown Patient';
      const reason = getLocalizedValue(apt.reason, currentLang);

      if (patientName.toLowerCase().includes(searchTerm) ||
        reason.toLowerCase().includes(searchTerm)) {
        searchResults.push({
          id: apt.id,
          title: patientName,
          subtitle: `${new Date(apt.date).toLocaleDateString()} - ${reason}`,
          type: 'appointment',
          icon: Calendar,
          route: `/appointments/${apt.id}`
        });
      }
    });

    // Search medical records
    medicalRecords.forEach(record => {
      if (!record || !record.diagnosis || !record.treatment) return;
      const patient = patients.find(p => p.id === record.patientId);
      const patientName = patient
        ? (resolvePatientName(patient as unknown as Record<string, unknown>, currentLang === 'ar') || 'Unknown Patient')
        : 'Unknown Patient';
      const diagnosis = getLocalizedValue(record.diagnosis, currentLang);
      const treatment = getLocalizedValue(record.treatment, currentLang);

      if (patientName.toLowerCase().includes(searchTerm) ||
        diagnosis.toLowerCase().includes(searchTerm) ||
        treatment.toLowerCase().includes(searchTerm)) {
        searchResults.push({
          id: record.id,
          title: diagnosis,
          subtitle: patientName,
          type: 'record',
          icon: FileText,
          route: `/records/${record.id}`
        });
      }
    });
  }

  // Filter commands based on query
  const filteredCommands = commands.filter(cmd => {
    const searchTerms = query.toLowerCase().split(' ');
    return searchTerms.every(term =>
      cmd.title.toLowerCase().includes(term) ||
      cmd.description.toLowerCase().includes(term) ||
      cmd.keywords.some(keyword => keyword.includes(term))
    );
  });

  const hasSearchResults = searchResults.length > 0;
  const hasCommands = filteredCommands.length > 0;
  const totalResults = searchResults.length + filteredCommands.length;

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev =>
            prev < totalResults - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev =>
            prev > 0 ? prev - 1 : totalResults - 1
          );
          break;
        case 'Enter':
          e.preventDefault();
          // Prioritize AI suggestion if available and high confidence
          if (aiSuggestion && aiSuggestion.confidence > 0.6) {
            executeAiCommand(aiSuggestion);
          } else if (selectedIndex < searchResults.length) {
            const result = searchResults[selectedIndex];
            navigate(result.route);
            onClose();
          } else {
            const cmdIndex = selectedIndex - searchResults.length;
            if (filteredCommands[cmdIndex]) {
              filteredCommands[cmdIndex].action();
            }
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, searchResults, filteredCommands, selectedIndex, totalResults, navigate, onClose, aiSuggestion]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      setQuery('');
      setSelectedIndex(0);
      setAiSuggestion(null);
    }
  }, [isOpen]);

  // Connect to AI Gateway singleton and subscribe to transcription events
  useEffect(() => {
    aiGatewayService.connect().catch((err) => {
      console.error('Failed to connect to AI Gateway:', err);
    });

    // Subscribe to transcription completion events (replaces voiceSocket 'chat-transcription')
    const unsubTranscription = aiGatewayService.on('assistant.transcription-complete', (data: { text: string; language: string }) => {
      console.log('Transcription received:', data.text);
      setQuery(data.text);
      setIsListening(false);
      setIsTranscribing(false);
    });

    // Subscribe to errors
    const unsubError = aiGatewayService.on('error', (error: any) => {
      // AI being unreachable is expected/recoverable — log calmly.
      if (error?.code === 'AI_UNAVAILABLE') {
        console.warn('AI gateway unavailable:', error?.message);
      } else {
        console.error('AI Gateway error:', error);
      }
      if (isListening || isTranscribing) {
        setIsListening(false);
        setIsTranscribing(false);
        alert(t('commandPalette.transcriptionError', { error: error.message || 'Unknown error' }));
      }
    });

    return () => {
      unsubTranscription();
      unsubError();
    };
  }, []);

  // Detect natural language queries and send to LLM
  useEffect(() => {
    // Clear existing timeout
    if (queryTimeoutRef.current) {
      clearTimeout(queryTimeoutRef.current);
    }

    // Reset AI suggestion when query changes
    setAiSuggestion(null);

    // Only parse if query looks like a command
    if (query.trim().length > 0 && looksLikeCommand(query)) {
      setAiParsing(true);

      // Debounce: Wait 500ms after user stops typing
      queryTimeoutRef.current = setTimeout(() => {
        console.log('Sending query to LLM:', query);
        console.log('Current page context:', pageContext);

        // Hard ceiling: if the gateway never resolves we still drop out
        // of the spinner so the regular results render (the symptom was
        // the palette stuck on "AI is understanding…" indefinitely).
        let settled = false;
        const watchdog = setTimeout(() => {
          if (settled) return;
          settled = true;
          console.warn('Command parse timed out after 10s — falling back to local results');
          setAiParsing(false);
        }, 10000);

        aiGatewayService.assistantParseCommand({
          query: query.trim(),
          context: {
            pageType: pageContext.pageType,
            patientId: pageContext.patientId,
            currentPatient: pageContext.currentPatient,
          },
        }).then((response: any) => {
          if (settled) return;
          settled = true;
          clearTimeout(watchdog);
          console.log('Command parsed:', response);
          setAiSuggestion(response);
          setAiParsing(false);
        }).catch((err: any) => {
          if (settled) return;
          settled = true;
          clearTimeout(watchdog);
          console.error('Command parsing failed:', err);
          setAiParsing(false);
        });
      }, 500);
    } else {
      setAiParsing(false);
    }

    return () => {
      if (queryTimeoutRef.current) {
        clearTimeout(queryTimeoutRef.current);
      }
    };
  }, [query, selectedPatient, pageContext]);

  // Helper: Detect if query looks like a command (vs simple search)
  const looksLikeCommand = (text: string): boolean => {
    const lower = text.toLowerCase().trim();

    // Action verbs that indicate commands
    const actionVerbs = [
      'schedule', 'book', 'create', 'make', 'add', 'new',
      'order', 'prescribe', 'write',
      'show', 'get', 'find', 'view', 'open', 'display',
      'analyze', 'review', 'check', 'interpret',
      'go', 'navigate', 'take',
      'record', 'note', 'document'
    ];

    // Check if starts with action verb
    const startsWithAction = actionVerbs.some(verb => lower.startsWith(verb));

    // Check if contains action verb anywhere
    const containsAction = actionVerbs.some(verb =>
      new RegExp(`\\b${verb}\\b`).test(lower)
    );

    // If it has an action verb OR is longer than 2 words, send to AI
    const wordCount = text.trim().split(/\s+/).length;

    return startsWithAction || containsAction || wordCount >= 3;
  };

  // Execute AI-parsed command
   
  const executeAiCommand = (suggestion: any) => {
    console.log('⚡ Executing AI command:', suggestion);
    console.log('📄 Current page context:', pageContext);
    console.log('🧑‍⚕️ Current patient from context:', pageContext?.currentPatient);
    console.log('📍 Page type:', pageContext?.pageType);

    // Build autofill data from OUR page context (frontend knows best)
    const buildAutofillData = () => {
       
      const autofill: any = {};

      console.log('═══════════════════════════════════════');
      console.log('🔧 BUILDING AUTOFILL DATA');
      console.log('═══════════════════════════════════════');
      console.log('📄 Page Context:', {
        pageType: pageContext.pageType,
        currentPage: pageContext.currentPage,
        patientId: pageContext.patientId,
        currentPatient: pageContext.currentPatient,
      });
      console.log('🤖 AI Suggestion Parameters:', suggestion.parameters);
      console.log('👤 Current User:', currentUser ? {
        id: currentUser.id,
        displayName: currentUser.displayName,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
      } : 'null');
      console.log('📋 Selected Patient (Store):', selectedPatient ? {
        id: selectedPatient.id,
        name: selectedPatient.name,
      } : 'null');

      // Patient Context Detection (with detailed logging)
      console.log('\n🔍 PATIENT CONTEXT DETECTION:');

      // PRIORITIZE selectedPatient (has real API data with firstName/lastName)
      if (selectedPatient) {
        console.log('  ✅ Source: SELECTED PATIENT FROM STORE (API data)');
        console.log('    - Patient ID:', selectedPatient.id);
        console.log('    - Patient has firstName?:', !!(selectedPatient as any).firstName);

        // Handle both API format (firstName/lastName) and mock format (name object)
        const patientName = (selectedPatient as any).firstName
          ? `${(selectedPatient as any).firstName} ${(selectedPatient as any).lastName}`.trim()
          : typeof selectedPatient.name === 'string'
            ? selectedPatient.name
            : getLocalizedValue(selectedPatient.name, currentLang);

        console.log('    - Patient Name:', patientName);

        autofill.patientId = selectedPatient.id;
        autofill.patientName = patientName;
        autofill.patientAge = pageContext.currentPatient?.age || 0;
        autofill.patientGender = (selectedPatient as any).gender || pageContext.currentPatient?.gender;
      } else if (pageContext.currentPatient) {
        console.log('  ⚠️ Source: FALLBACK TO PAGE CONTEXT (might have Unknown)');
        console.log('    - Patient ID:', pageContext.currentPatient.id);
        console.log('    - Patient Name:', pageContext.currentPatient.name);
        console.log('    - Patient Age:', pageContext.currentPatient.age);
        console.log('    - Patient Gender:', pageContext.currentPatient.gender);

        autofill.patientId = pageContext.currentPatient.id;
        autofill.patientName = pageContext.currentPatient.name;
        autofill.patientAge = pageContext.currentPatient.age;
        autofill.patientGender = pageContext.currentPatient.gender;
      } else {
        // Fallback: AI may have extracted a patient name from the query
        // (e.g. "schedule an appointment with ahmed for thursday"). Try to
        // resolve that to an actual patient in the loaded list before
        // navigating, so the destination form pre-fills correctly. Without
        // this the home palette would route to /appointments/new with
        // only `{ patientName: "ahmed" }` and no id.
        const params = suggestion.parameters || {};
        const candidateName: string =
          params.patientName ||
          params.patient ||
          params.patient_name ||
          '';
        const matched = candidateName
          ? findPatientByName(patients as unknown as Array<Record<string, unknown>>, candidateName)
          : undefined;
        if (matched) {
          console.log('  ✅ Source: RESOLVED FROM AI PARAMETERS via name lookup');
          autofill.patientId = (matched as { id: string }).id;
          autofill.patientName = resolvePatientName(matched, currentLang === 'ar');
        } else {
          console.log('  ❌ NO PATIENT CONTEXT DETECTED!');
          console.log('    - pageContext.currentPatient: null');
          console.log('    - selectedPatient (store): null');
          console.log('    - Tried name lookup with:', candidateName || '(none)');
          console.log('    ⚠️ Patient autofill will be sparse');
        }
      }

      // Provider Context Detection
      console.log('\n🔍 PROVIDER CONTEXT DETECTION:');

      if (currentUser && !suggestion.parameters?.provider) {
        console.log('  ✅ Defaulting to current logged-in user');
        console.log('    - Provider ID:', currentUser.id);
        console.log('    - Provider Name:', currentUser.displayName);

        autofill.providerId = currentUser.id;
        autofill.providerName = currentUser.displayName || `${currentUser.firstName} ${currentUser.lastName}`;
      } else if (suggestion.parameters?.provider) {
        console.log('  ✅ Provider specified in command:', suggestion.parameters.provider);
      } else {
        console.log('  ❌ No provider context (not logged in?)');
      }

      // Merge AI Parameters
      console.log('\n🔍 MERGING AI PARAMETERS:');
      if (suggestion.parameters) {
        console.log('  Parameters to merge:', suggestion.parameters);
        Object.assign(autofill, suggestion.parameters);
      }

      console.log('\n📦 FINAL AUTOFILL DATA:', autofill);
      console.log('═══════════════════════════════════════\n');

      return autofill;
    };

    switch (suggestion.command) {
      case 'navigate':
        navigate(suggestion.parameters.route);
        break;

      case 'search_patient':
        console.log('🔍 Navigating to patients page with search query:', suggestion.parameters.query);
        navigate(`/patients?search=${encodeURIComponent(suggestion.parameters.query)}`);
        break;

      case 'view_patient':
        if (suggestion.parameters.patientId) {
          navigate(`/patients/${suggestion.parameters.patientId}`);
        } else {
          // Search by name
          navigate(`/patients?search=${encodeURIComponent(suggestion.parameters.patientName)}`);
        }
        break;

      case 'view_labs':
        if (suggestion.parameters.patientName && selectedPatient) {
          navigate(`/lab-orders?patientId=${selectedPatient.id}`);
        } else {
          navigate('/lab-orders');
        }
        break;

      case 'schedule_appointment': {
        const appointmentData = buildAutofillData();
        console.log('📅 Scheduling appointment with autofill:', appointmentData);
        console.log('📅 Navigating to /appointments/new');

        // Use replace to force a fresh navigation state
        navigate('/appointments/new', {
          state: appointmentData,
          replace: true, // Replace current entry to ensure state update
        });
        break;
      }

      case 'order_lab_tests':
      case 'create_lab_order': {
        const labData = buildAutofillData();
        console.log('🔬 Creating lab order with autofill:', labData);
        navigate('/lab-orders/new', {
          state: labData,
        });
        break;
      }

      case 'create_prescription':
      case 'prescribe_medication': {
        const prescriptionData = buildAutofillData();
        console.log('💊 Creating prescription with autofill:', prescriptionData);
        navigate('/prescriptions/new', {
          state: prescriptionData,
        });
        break;
      }

      case 'create_medical_record': {
        const medicalRecordData = buildAutofillData();
        console.log('📝 Creating medical record with autofill:', medicalRecordData);

        // Set return path for voice navigation if we have a patient context
        if (medicalRecordData.patientId) {
          useVoiceNavigationStore.getState().setReturnPath(`/patients/${medicalRecordData.patientId}`);
        }

        if (medicalRecordData.patientId) {
          navigate(`/patients/${medicalRecordData.patientId}?record=new`, {
            state: medicalRecordData,
          });
        } else {
          navigate('/patients', { state: medicalRecordData });
        }
        break;
      }

      case 'analyze':
        console.log('🧠 Analysis request - this should open AI chat bubble or show analysis');
        // TODO: Open AI chat bubble with context
        // For now, just show an alert
        alert(t('commandPalette.analysisComingSoon', { context: pageContext.pageType, patient: pageContext.currentPatient?.name || 'Unknown' }));
        break;

      case 'search':
      default:
        // Fallback to normal search
        break;
    }

    onClose();
  };

  // Voice input handler using AI Gateway (Whisper backend via singleton connection)
  const handleVoiceInput = async () => {
    if (!aiGatewayService.isConnected()) {
      alert(t('commandPalette.voiceServiceNotConnected'));
      return;
    }

    // If already listening, stop recording
    if (isListening) {
      stopRecording();
      return;
    }

    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      console.log('Microphone access granted');

      // Create MediaRecorder with WebM codec (supported by Whisper)
      const options = { mimeType: 'audio/webm;codecs=opus' };
      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      // Start session via AI Gateway
      const sessionId = `command-voice-${Date.now()}`;
      aiGatewayService.assistantStartSession({
        sessionId,
        transcriptionOnly: true, // We only want transcription, not SOAP generation
        language: currentLang,
      });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        console.log('Recording stopped, sending to Whisper...');

        // Combine all audio chunks
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });

        // Convert to ArrayBuffer for socket transmission
        const arrayBuffer = await audioBlob.arrayBuffer();

        // Send audio chunk and end-recording via AI Gateway
        aiGatewayService.assistantSendAudioChunk({
          audioData: arrayBuffer,
          sessionId,
        });

        aiGatewayService.assistantEndRecording({
          sessionId,
        });

        // Stop all tracks to release microphone
        stream.getTracks().forEach(track => track.stop());

        // Clear chunks
        audioChunksRef.current = [];
      };

      // Start recording
      mediaRecorder.start(100); // Collect data every 100ms
      setIsListening(true);
      console.log('Recording started...');

      // Auto-stop after 10 seconds (prevent infinite recording)
      setTimeout(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          console.log('Auto-stopping after 10 seconds');
          stopRecording();
        }
      }, 10000);

    } catch (error: unknown) {
      console.error('Microphone access error:', error);
      setIsListening(false);

      const err = error as { name?: string; message?: string };
      if (err.name === 'NotAllowedError') {
        alert(t('commandPalette.micAccessDenied'));
      } else if (err.name === 'NotFoundError') {
        alert(t('commandPalette.micNotFound'));
      } else {
        alert(t('commandPalette.voiceRecordingFailed', { error: err.message || 'Unknown error' }));
      }
    }
  };

  // Stop recording helper
  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsListening(false);
      setIsTranscribing(true); // Backend is now transcribing
      console.log('🎤 Stopping recording, starting transcription...');
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop. z-[60] keeps the palette above the AI assistant bubble
          (z-50) so the search overlays the brain, not the other way round. */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[60]"
        onClick={onClose}
      />

      {/* Command Palette */}
      <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl z-[60]">
        <div
          className="bg-white rounded-xl shadow-2xl overflow-hidden"
          style={{ backgroundColor: 'var(--color-bg-primary)' }}
        >
          {/* Search Header */}
          <div className="p-4 border-b" style={{ borderColor: 'var(--color-border-primary)' }}>
            <div className="flex items-center gap-x-3">
              <Brain className="w-5 h-5" style={{ color: 'var(--color-primary-600)' }} />
              {/* The bare input would otherwise pick up the global
                  `:focus-visible { outline: 2px solid primary }` ring
                  from index.css and render a floating blue rectangle
                  around just the text field — visually disconnected
                  from the palette card. The palette modal itself is
                  the focus context (Cmd+K opened it, the entire card
                  is in focus); the input doesn't need its own ring.
                  `focus-visible:outline-none` overrides the global
                  via class specificity. */}
              <TextInput
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={isListening ? t('commandPalette.recording') : isTranscribing ? t('commandPalette.transcribing') : t('commandPalette.placeholder')}
                disabled={isListening || isTranscribing}
                className="flex-1 min-h-0 px-0 border-0 rounded-none outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 bg-transparent text-lg"
                style={{
                  backgroundColor: 'transparent',
                  borderColor: 'transparent',
                  opacity: isListening || isTranscribing ? 0.7 : 1
                }}
              />
              <Button
                variant={isListening ? 'destructive' : 'secondary'}
                size="icon-sm"
                onClick={handleVoiceInput}
                disabled={isTranscribing}
                className={isListening ? 'animate-pulse' : ''}
                title={isListening ? t('commandPalette.voiceTooltipStop') : isTranscribing ? t('commandPalette.voiceTooltipTranscribing') : t('commandPalette.voiceTooltipStart')}
              >
                <Mic />
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={onClose}
                aria-label={t('common.close')}
              >
                <X />
              </Button>
            </div>
          </div>

          {/* Results */}
          <div className="max-h-96 overflow-y-auto">
            {/* AI Suggestion (if available) */}
            {aiSuggestion && aiSuggestion.confidence > 0.6 && (
              <div className="p-3 border-b" style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'rgba(139, 92, 246, 0.1)' }}>
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4" style={{ color: 'var(--color-primary-600)' }} />
                  <span className="text-xs font-semibold" style={{ color: 'var(--color-primary-600)' }}>
                    {t('commandPalette.aiUnderstood')}
                  </span>
                </div>
                <button
                  onClick={() => executeAiCommand(aiSuggestion)}
                  className="w-full px-4 py-3 rounded-lg flex items-center justify-between transition-colors hover:bg-opacity-80"
                  style={{ backgroundColor: 'rgba(139, 92, 246, 0.15)' }}
                >
                  <div className="flex-1 text-start">
                    <p className="font-medium text-sm" style={{ color: 'var(--color-text-primary)' }}>
                      {aiSuggestion.displayText || `Execute: ${aiSuggestion.command}`}
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-tertiary)' }}>
                      {t('commandPalette.pressEnter')}
                    </p>
                  </div>
                  <span className="text-xs px-2 py-1 rounded"
                    style={{
                      backgroundColor: 'var(--color-primary-600)',
                      color: 'white'
                    }}
                  >
                    ⏎ Enter
                  </span>
                </button>
              </div>
            )}

            {/* AI Parsing Indicator */}
            {aiParsing && (
              <div className="p-4 flex items-center gap-3" style={{ color: 'var(--color-text-tertiary)' }}>
                <Sparkles className="w-4 h-4 animate-pulse" style={{ color: 'var(--color-primary-600)' }} />
                <span className="text-sm">{t('commandPalette.aiUnderstanding')}</span>
              </div>
            )}

            {totalResults === 0 && !aiSuggestion && !aiParsing ? (
              <div className="p-8 text-center">
                <p style={{ color: 'var(--color-text-tertiary)' }}>
                  {t('commandPalette.noResults')}
                </p>
              </div>
            ) : (
              <div className="py-2">
                {/* Search Results */}
                {hasSearchResults && (
                  <>
                    <div className="px-4 py-1 text-xs font-medium" style={{ color: 'var(--color-text-tertiary)' }}>
                      {t('commandPalette.searchResults')}
                    </div>
                    {searchResults.map((result, index) => {
                      const Icon = result.icon;
                      const isSelected = index === selectedIndex;

                      return (
                        <button
                          key={`search-${result.type}-${result.id}`}
                          onClick={() => {
                            navigate(result.route);
                            onClose();
                          }}
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 flex items-center gap-x-2 sm:gap-x-3 transition-colors"
                          style={{
                            backgroundColor: isSelected ? 'var(--color-bg-tertiary)' : 'transparent'
                          }}
                          onMouseEnter={() => setSelectedIndex(index)}
                        >
                          <div
                            className="p-2 rounded-lg"
                            style={{ backgroundColor: 'var(--color-bg-secondary)' }}
                          >
                            <Icon className="w-5 h-5" style={{ color: 'var(--color-primary-600)' }} />
                          </div>
                          <div className="flex-1 text-start">
                            <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                              {result.title}
                            </p>
                            <p className="text-sm" style={{ color: 'var(--color-text-tertiary)' }}>
                              {result.subtitle}
                            </p>
                          </div>
                          <span className="text-xs px-2 py-1 rounded capitalize"
                            style={{
                              backgroundColor: 'var(--color-bg-secondary)',
                              color: 'var(--color-text-secondary)'
                            }}
                          >
                            {result.type}
                          </span>
                        </button>
                      );
                    })}
                  </>
                )}

                {/* Commands */}
                {hasCommands && (
                  <>
                    {hasSearchResults && <div className="my-2 border-t" style={{ borderColor: 'var(--color-border-primary)' }} />}
                    <div className="px-4 py-1 text-xs font-medium" style={{ color: 'var(--color-text-tertiary)' }}>
                      {t('commandPalette.commands')}
                    </div>
                    {filteredCommands.map((cmd, index) => {
                      const Icon = cmd.icon;
                      const adjustedIndex = searchResults.length + index;
                      const isSelected = adjustedIndex === selectedIndex;

                      return (
                        <button
                          key={cmd.id}
                          onClick={cmd.action}
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 flex items-center gap-x-2 sm:gap-x-3 transition-colors"
                          style={{
                            backgroundColor: isSelected ? 'var(--color-bg-tertiary)' : 'transparent'
                          }}
                          onMouseEnter={() => setSelectedIndex(adjustedIndex)}
                        >
                          <div
                            className="p-2 rounded-lg"
                            style={{ backgroundColor: 'var(--color-bg-secondary)' }}
                          >
                            <Icon className="w-5 h-5" style={{ color: 'var(--color-primary-600)' }} />
                          </div>
                          <div className="flex-1 text-start">
                            <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                              {cmd.title}
                            </p>
                            <p className="text-sm" style={{ color: 'var(--color-text-tertiary)' }}>
                              {cmd.description}
                            </p>
                          </div>
                          {isSelected && (
                            <span className="text-xs px-2 py-1 rounded"
                              style={{
                                backgroundColor: 'var(--color-bg-secondary)',
                                color: 'var(--color-text-tertiary)'
                              }}
                            >
                              Enter
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-3 border-t flex items-center justify-between text-xs"
            style={{
              borderColor: 'var(--color-border-primary)',
              color: 'var(--color-text-tertiary)'
            }}
          >
            <div className="flex items-center gap-x-4">
              <span>{t('commandPalette.navigate')}</span>
              <span>{t('commandPalette.select')}</span>
              <span>{t('commandPalette.close')}</span>
            </div>
            <span>{t('commandPalette.openShortcut')}</span>
          </div>
        </div>
      </div>
    </>
  );
};